import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { extractErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
  Sparkles, Instagram, Facebook, Linkedin, Camera, UploadCloud, Loader2,
  CheckCircle2, AlertTriangle, Calendar as CalendarIcon, Image as ImageIcon,
  ChevronRight, Settings, Edit3, X, Send, Layers, PenSquare, Wand2, Eye,
  ChevronDown, Plus, HelpCircle, RefreshCw, ShieldAlert,
} from 'lucide-react';

/**
 * MarketingStudio.js — Community Manager IA (interface simplifiée).
 *
 * L'utilisateur ne voit PAS :
 *   Brand Brain, Strategist, Planner (interne), Content Inbox (interne),
 *   Vision, Copywriter, Semantic Guard, Reviewer, capabilities registry,
 *   LLM calls, fact_ledger, semantic_integrity, source_mix, candidates,
 *   piliers % éditoriaux, versions V2.x.
 *
 * L'utilisateur voit :
 *   - Un assistant Community Manager avec 3 actions simples.
 *   - Des posts prêts à valider (previews Instagram/Facebook/LinkedIn).
 *   - Un calendrier simple, une bibliothèque simple, des insights (non-priorisés).
 *
 * L'interface technique reste disponible via
 *   Paramètres agent > Détails avancés → /marketing/advanced
 */

const PLATFORM_META = {
  instagram: {
    label: 'Instagram',
    icon: Instagram,
    accent: 'from-pink-500 via-sky-400 to-orange-400',
    tag: 'bg-pink-500/10 text-pink-700 border-pink-200',
  },
  facebook: {
    label: 'Facebook',
    icon: Facebook,
    accent: 'from-sky-500 to-blue-600',
    tag: 'bg-blue-500/10 text-blue-700 border-blue-200',
  },
  linkedin: {
    label: 'LinkedIn',
    icon: Linkedin,
    accent: 'from-blue-600 to-blue-800',
    tag: 'bg-blue-600/10 text-blue-700 border-blue-200',
  },
};

const TABS = [
  { id: 'agent',    label: 'Agent IA' },
  { id: 'validate', label: 'À valider' },
  { id: 'calendar', label: 'Calendrier' },
  { id: 'library',  label: 'Bibliothèque' },
  { id: 'insights', label: 'Insights' },
];

export default function MarketingStudio() {
  const [view, setView] = useState('agent');
  const [brand, setBrand] = useState(null);
  const [posts, setPosts] = useState([]);
  const [summary, setSummary] = useState({
    total: 0, ready_to_validate: 0, attention_needed: 0, not_reviewed: 0,
  });
  const [preparing, setPreparing] = useState(false);
  const [validating, setValidating] = useState(null);
  const [generating, setGenerating] = useState({});             // {`${draftId}:${platform}`: true}
  const [inboxUploading, setInboxUploading] = useState(false);
  const [inboxText, setInboxText] = useState('');
  const [inboxFiles, setInboxFiles] = useState([]);
  const [postPlatformTab, setPostPlatformTab] = useState({});
  const [expandedDetails, setExpandedDetails] = useState({});
  const [editing, setEditing] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [scheduling, setScheduling] = useState(null);   // {post} en cours de programmation
  // V3.1d — warnings du plan courant (banner amber persistant)
  const [planWarnings, setPlanWarnings] = useState([]);
  // V3.1d — statut LinkedIn (page Reno Resolve verrouillée backend)
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  // V3.1d — sélection publication persistée par draft
  //   Miroir local (state React) synchronisé avec draft.selected_platforms.
  //   Optimistic UI + rollback si PATCH échoue.
  const [selectedPlatformsByDraft, setSelectedPlatformsByDraft] = useState({});
  const [savingSelectedByDraft, setSavingSelectedByDraft] = useState({});

  // -------------------------------------------------------------------
  // Data loaders
  // -------------------------------------------------------------------
  const loadBrand = useCallback(async () => {
    try {
      const r = await api.get('/community-manager/brand-summary');
      setBrand(r.data?.brand || null);
    } catch (_) { /* silent */ }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      const r = await api.get('/community-manager/pending-posts');
      setPosts(r.data?.posts || []);
      setSummary(r.data?.summary || {
        total: 0, ready_to_validate: 0, attention_needed: 0, not_reviewed: 0,
      });
      setPlanWarnings(r.data?.plan_warnings || []);
    } catch (_) { /* silent */ }
  }, []);

  // V3.1d — Statut LinkedIn (verrouillé backend sur Reno Resolve org URN)
  const loadLinkedinStatus = useCallback(async () => {
    try {
      const r = await api.get('/linkedin/status');
      setLinkedinStatus(r.data || null);
    } catch (_) { setLinkedinStatus(null); }
  }, []);

  useEffect(() => {
    loadBrand(); loadPosts(); loadLinkedinStatus();
  }, [loadBrand, loadPosts, loadLinkedinStatus]);

  // -------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------
  const prepareWeek = async ({ forceRegenerate = false } = {}) => {
    if (forceRegenerate) {
      const ok = window.confirm(
        "Créer une nouvelle sélection d'idées ?\n\n"
        + "Les sujets actuels seront conservés dans l'historique afin d'éviter "
        + "de les reproposer immédiatement.",
      );
      if (!ok) return;
    }
    setPreparing(true);
    try {
      const r = await api.post('/community-manager/prepare-week', {
        force_regenerate: !!forceRegenerate,
      });
      if (r.data?.needs_setup) {
        toast.warning(r.data.message || "Un plan est nécessaire avant de préparer la semaine.");
      } else {
        const s = r.data?.summary || {};
        // V3.1f — Toast honnête distinguant created / reused / blocked / rejected
        const parts = [];
        if (s.planned !== undefined) parts.push(`${s.planned} idée(s) prévue(s)`);
        if (s.created !== undefined && s.created > 0) parts.push(`${s.created} créée(s)`);
        if (s.reused !== undefined && s.reused > 0) parts.push(`${s.reused} réutilisée(s)`);
        if (s.blocked !== undefined && s.blocked > 0) parts.push(`${s.blocked} bloquée(s)`);
        if (s.rejected !== undefined && s.rejected > 0) parts.push(`${s.rejected} rejetée(s)`);
        if (s.approved !== undefined && s.approved > 0) parts.push(`${s.approved} validée(s)`);
        if (s.scheduled !== undefined && s.scheduled > 0) parts.push(`${s.scheduled} programmée(s)`);
        const detail = parts.length
          ? parts.join(' · ')
          : `${s.total || 0} post(s) · ${s.ready_to_validate || 0} vérifié(s)`;
        toast.success(
          `Semaine ${forceRegenerate ? 'régénérée' : 'prête'} — ${detail}`
        );
        setPlanWarnings(r.data?.warnings || []);
        try { await loadPosts(); } catch (e) { console.warn('loadPosts failed', e); }
        setView('validate');
      }
    } catch (e) {
      toast.error(extractErrorMessage(e, "Préparation impossible"));
    }
    setPreparing(false);
  };

  const quickAddFieldContent = async () => {
    if (!inboxFiles.length && !inboxText.trim()) {
      toast.error('Ajoutez au moins une photo ou décrivez ce qui s\'est passé.');
      return;
    }
    setInboxUploading(true);
    try {
      const form = new FormData();
      form.append('raw_text', inboxText);
      (inboxFiles || []).forEach(f => form.append('files', f));
      const r = await api.post('/community-manager/inbox/quick-add', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data?.success) {
        toast.success('Contenu ajouté. L\'agent s\'occupe du reste.');
        setInboxText('');
        setInboxFiles([]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ajout impossible");
    }
    setInboxUploading(false);
  };

  const validatePost = async (post) => {
    if (!post?.review?.can_validate) {
      toast.warning("Ce post nécessite votre attention avant validation.");
      return;
    }
    setValidating(post.draft_id);
    try {
      await api.post(`/social-agent/drafts/${post.draft_id}/approve`, {});
      toast.success('Publication validée');
      await loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Validation refusée');
    }
    setValidating(null);
  };

  const rejectPost = async (post) => {
    const reason = window.prompt('Raison du rejet ? (optionnel)') || '';
    setValidating(post.draft_id);
    try {
      await api.post(`/social-agent/drafts/${post.draft_id}/reject`, { reason });
      toast.success('Publication rejetée');
      await loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rejet impossible');
    }
    setValidating(null);
  };

  // V3.0 Bloc 1 — Programmation
  const submitSchedule = async ({ draft_id, scheduled_at_local, timezone, platforms, isReschedule }) => {
    try {
      const url = isReschedule
        ? `/community-manager/drafts/${draft_id}/reschedule`
        : `/community-manager/drafts/${draft_id}/schedule`;
      await api.post(url, { scheduled_at_local, timezone, platforms });
      toast.success(isReschedule ? 'Programmation modifiée' : 'Publication programmée');
      setScheduling(null);
      await loadPosts();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || 'Programmation impossible');
    }
  };

  const unschedulePost = async (post) => {
    if (!window.confirm('Déprogrammer ce post ?')) return;
    try {
      await api.post(`/community-manager/drafts/${post.draft_id}/unschedule`, {});
      toast.success('Post déprogrammé');
      await loadPosts();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || 'Déprogrammation impossible');
    }
  };

  // V3.1d — Sélection publication persistée par draft
  /** Retourne la liste de plateformes cochées pour un draft.
   *  Source canonique = draft.selected_platforms (backend). Fallback pour
   *  les anciens drafts (null) = toutes les plateformes ayant une variante. */
  const getSelectedPlatforms = (post) => {
    if (!post) return [];
    if (selectedPlatformsByDraft[post.draft_id] !== undefined) {
      return selectedPlatformsByDraft[post.draft_id];
    }
    if (Array.isArray(post.selected_platforms) && post.selected_platforms.length) {
      return post.selected_platforms;
    }
    return (post.platform_variants || []).map(v => v.platform);
  };

  /** Bascule d'une plateforme + persistance backend (optimistic UI). */
  const toggleDraftSelectedPlatform = async (post, platform) => {
    const available = (post.platform_variants || []).map(v => v.platform);
    if (!available.includes(platform)) {
      toast.error(`Aucune variante '${platform}' pour ce post`);
      return;
    }
    const current = getSelectedPlatforms(post);
    const has = current.includes(platform);
    const next = has ? current.filter(p => p !== platform) : [...current, platform];
    if (next.length === 0) {
      toast.error("Sélectionne au moins une plateforme.");
      return;
    }
    setSelectedPlatformsByDraft(prev => ({ ...prev, [post.draft_id]: next }));
    setSavingSelectedByDraft(prev => ({ ...prev, [post.draft_id]: true }));
    try {
      const r = await api.patch(`/social-agent/drafts/${post.draft_id}/selected-platforms`, {
        selected_platforms: next,
      });
      const updated = r.data?.draft;
      if (updated) {
        setPosts(prev => prev.map(p =>
          p.draft_id === updated.draft_id
            ? { ...p, selected_platforms: updated.selected_platforms }
            : p,
        ));
      }
    } catch (e) {
      setSelectedPlatformsByDraft(prev => ({ ...prev, [post.draft_id]: current }));
      toast.error(e?.response?.data?.detail || 'Sauvegarde de la sélection échouée');
    }
    setSavingSelectedByDraft(prev => ({ ...prev, [post.draft_id]: false }));
  };


  // V2.9.1 — Génération manuelle du visuel pour UNE plateforme donnée.
  // 1 seul appel provider ; les autres plateformes sont adaptées localement.
  // V2.9.5 — Job async persisté : POST retourne generation_job_id + status.
  //          On poll ensuite GET /generation-jobs/{id} jusqu'à completed/failed.
  const pollGenerationJob = async (jobId, post) => {
    const start = Date.now();
    const maxMs = 4 * 60 * 1000;                // 4 min max côté UI
    while (Date.now() - start < maxMs) {
      await new Promise(r => setTimeout(r, 2500));
      try {
        const jr = await api.get(`/community-manager/generation-jobs/${jobId}`);
        const s = jr.data?.status;
        if (s === 'completed') {
          // Rafraîchit uniquement CE post
          const pr = await api.get('/community-manager/pending-posts');
          const fresh = (pr.data?.posts || []).find(
            p => p.draft_id === post.draft_id,
          );
          if (fresh) {
            setPosts(prev => prev.map(p =>
              p.draft_id === post.draft_id ? fresh : p,
            ));
          }
          toast.success('Visuel créé');
          return true;
        }
        if (s === 'failed') {
          const code = jr.data?.error_code;
          const msg = jr.data?.error_message || 'Impossible de générer le visuel';
          if (code === 'insufficient_credits') {
            toast.error('Crédits Lovart insuffisants (ou file d\'attente pleine)');
          } else if (code === 'provider_timeout') {
            toast.error('Lovart met plus de temps que prévu');
          } else if (code === 'provider_refused') {
            toast.error("Lovart n'a pas pu générer ce visuel");
          } else {
            toast.error(msg);
          }
          return false;
        }
      } catch (_) { /* ignore transient errors */ }
    }
    toast.warning('Génération toujours en cours — reviens plus tard');
    return false;
  };

  const generateVisualForPost = async (post, platform, opts = {}) => {
    setGenerating(prev => ({ ...prev, [`${post.draft_id}:${platform}`]: true }));
    try {
      const r = await api.post(
        `/community-manager/posts/${post.draft_id}/generate-visual`,
        { platform, sync: false, force: !!opts.force },
      );
      const jobId = r.data?.generation_job_id;
      if (r.data?.reused_job) {
        toast.info('Génération déjà en cours…');
      } else if (opts.force) {
        toast.info('Nouvelle image en cours de génération…');
      } else {
        toast.info('Création du visuel…');
      }
      if (jobId) {
        await pollGenerationJob(jobId, post);
      } else if (r.data?.post) {
        setPosts(prev => prev.map(p =>
          p.draft_id === post.draft_id ? r.data.post : p,
        ));
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        toast.error(detail || "Corrigez le post avant de générer un visuel");
      } else {
        toast.error(detail || "Impossible de générer le visuel");
      }
    }
    setGenerating(prev => {
      const next = { ...prev };
      delete next[`${post.draft_id}:${platform}`];
      return next;
    });
  };

  const validateAll = async () => {
    const validatables = posts.filter(p => p.review?.can_validate);
    if (!validatables.length) {
      toast.info('Aucun post prêt à valider.');
      return;
    }
    if (!window.confirm(`Valider ${validatables.length} publication(s) ?`)) return;
    let ok = 0;
    for (const p of validatables) {
      try {
        await api.post(`/social-agent/drafts/${p.draft_id}/approve`, {});
        ok += 1;
      } catch (_) { /* continue */ }
    }
    toast.success(`${ok}/${validatables.length} publications validées`);
    await loadPosts();
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/social-agent/drafts/${editing.draft_id}`, {
        master_message: editing.master_message,
        platform_variants: editing.platform_variants,
      });
      toast.success('Modifications enregistrées');
      setEditing(null);
      await loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    }
  };

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8" data-testid="marketing-studio">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"
              data-testid="mkt-header-title">
            <Sparkles className="w-7 h-7 text-blue-700" />
            Community Manager IA
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Votre agent prépare, vérifie et organise vos contenus pour les réseaux sociaux.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSettingsOpen(true)}
            className="text-slate-500 hover:text-slate-800 p-2 rounded-lg hover:bg-slate-100"
            title="Paramètres de l'agent"
            data-testid="mkt-settings-btn"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => { setView(t.id); if (t.id === 'validate') loadPosts(); }}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              view === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid={`mkt-tab-${t.id}`}
          >
            {t.label}
            {t.id === 'validate' && summary.total > 0 && (
              <span className="ml-1.5 text-[10px] bg-blue-700 text-white rounded-full px-1.5 py-0.5">
                {summary.total}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* AGENT IA */}
      {view === 'agent' && (
        <AgentTab
          preparing={preparing}
          onPrepareWeek={prepareWeek}
          onCreatePost={() => setView('validate')}
          inboxText={inboxText}
          setInboxText={setInboxText}
          inboxFiles={inboxFiles}
          setInboxFiles={setInboxFiles}
          inboxUploading={inboxUploading}
          onQuickAdd={quickAddFieldContent}
          summary={summary}
          onGoToValidate={() => setView('validate')}
        />
      )}

      {/* À VALIDER */}
      {view === 'validate' && (
        <ValidateTab
          posts={posts}
          brand={brand}
          summary={summary}
          planWarnings={planWarnings}
          linkedinStatus={linkedinStatus}
          postPlatformTab={postPlatformTab}
          setPostPlatformTab={setPostPlatformTab}
          expandedDetails={expandedDetails}
          setExpandedDetails={setExpandedDetails}
          onValidate={validatePost}
          onReject={rejectPost}
          onEdit={setEditing}
          onValidateAll={validateAll}
          validating={validating}
          onRefresh={loadPosts}
          onGenerateVisual={generateVisualForPost}
          generating={generating}
          onSchedule={(p) => setScheduling({ post: p, mode: 'schedule' })}
          onReschedule={(p) => setScheduling({ post: p, mode: 'reschedule' })}
          onUnschedule={unschedulePost}
          getSelectedPlatforms={getSelectedPlatforms}
          onTogglePlatform={toggleDraftSelectedPlatform}
          savingSelectedByDraft={savingSelectedByDraft}
          onFindingConfirmed={() => { try { loadPosts(); } catch (_) {} }}
        />
      )}

      {/* CALENDRIER */}
      {view === 'calendar' && <CalendarTabV2 />}

      {/* BIBLIOTHÈQUE */}
      {view === 'library' && <LibraryTabPlaceholder />}

      {/* INSIGHTS */}
      {view === 'insights' && <InsightsTabPlaceholder />}

      {/* EDITOR MODAL */}
      {editing && (
        <EditorModal editing={editing} setEditing={setEditing} onSave={saveEdit} />
      )}

      {/* SCHEDULE MODAL */}
      {scheduling && (
        <ScheduleModal
          scheduling={scheduling}
          initialSelected={getSelectedPlatforms(scheduling.post)}
          onClose={() => setScheduling(null)}
          onSubmit={submitSchedule}
        />
      )}

      {/* SETTINGS PANEL */}
      {settingsOpen && (
        <SettingsPanel onClose={() => setSettingsOpen(false)} brand={brand} />
      )}
    </div>
  );
}

// ==========================================================================
// AGENT IA — actions principales simples
// ==========================================================================
function AgentTab({
  preparing, onPrepareWeek, onCreatePost,
  inboxText, setInboxText, inboxFiles, setInboxFiles,
  inboxUploading, onQuickAdd, summary, onGoToValidate,
}) {
  return (
    <div className="space-y-8" data-testid="mkt-agent-view">
      {/* Action principale — Préparer ma semaine */}
      <div className="rounded-2xl p-8 text-white shadow-lg relative overflow-hidden"
           style={{ background: 'linear-gradient(135deg, #1C49A6 0%, #163C89 100%)' }}
           data-testid="mkt-prepare-week-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider bg-white/20 backdrop-blur rounded-full px-3 py-1 mb-3">
              <Sparkles className="w-3 h-3" /> Assistant IA · Reno Resolve
            </div>
            <h2 className="text-2xl font-bold">Préparer ma semaine</h2>
            <p className="text-white/90 mt-2 leading-relaxed">
              L'agent réutilise le plan existant, complète les publications manquantes
              et vérifie chaque contenu. Idempotent : rien n'est régénéré inutilement.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              onClick={() => onPrepareWeek({ forceRegenerate: false })}
              disabled={preparing}
              className="bg-white hover:bg-white/90 disabled:opacity-60 px-6 py-3 rounded-xl font-semibold shadow-md flex items-center gap-2"
              style={{ color: '#1C49A6' }}
              data-testid="mkt-prepare-week-btn"
            >
              {preparing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
              {preparing ? 'Préparation…' : 'Préparer ma semaine'}
            </button>
            {summary.total > 0 && (
              <button
                onClick={() => onPrepareWeek({ forceRegenerate: true })}
                disabled={preparing}
                className="bg-white/15 hover:bg-white/25 disabled:opacity-60 px-5 py-3 rounded-xl font-semibold text-white border border-white/30 flex items-center gap-2 text-sm"
                data-testid="mkt-regenerate-week-btn"
                title="Ignore le cache et propose de nouveaux angles éditoriaux"
              >
                <RefreshCw className="w-4 h-4" /> Régénérer la semaine
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Résumé */}
      {summary.total > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5"
             data-testid="mkt-week-summary">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Cette semaine</div>
              <div className="mt-2 flex items-center gap-6 flex-wrap">
                <div>
                  <div className="text-3xl font-bold text-slate-900">{summary.total}</div>
                  <div className="text-xs text-slate-500">publication(s) prévue(s)</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-emerald-600">{summary.ready_to_validate}</div>
                  <div className="text-xs text-slate-500">prête(s) à valider</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-amber-600">{summary.attention_needed}</div>
                  <div className="text-xs text-slate-500">nécessite(nt) votre attention</div>
                </div>
              </div>
            </div>
            <button
              onClick={onGoToValidate}
              className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
              data-testid="mkt-goto-validate-btn"
            >
              Voir les publications <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Actions secondaires */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={onCreatePost}
          className="bg-white border border-slate-200 hover:border-blue-300 rounded-2xl p-5 text-left flex items-start gap-4 transition-colors"
          data-testid="mkt-create-post-btn"
        >
          <div className="bg-blue-100 text-blue-800 rounded-xl p-2.5">
            <PenSquare className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-slate-900">Créer un post</div>
            <div className="text-sm text-slate-500 mt-0.5">
              Pour une publication ponctuelle en dehors du plan hebdomadaire.
            </div>
          </div>
        </button>

        <div className="bg-white border border-slate-200 rounded-2xl p-5"
             data-testid="mkt-add-field-card">
          <div className="flex items-start gap-4 mb-3">
            <div className="bg-emerald-100 text-emerald-700 rounded-xl p-2.5">
              <Camera className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold text-slate-900">Ajouter du contenu terrain</div>
              <div className="text-sm text-slate-500 mt-0.5">
                Photos, vidéos, documents… L'agent s'occupe de l'analyse.
              </div>
            </div>
          </div>
          <FieldContentDrop
            files={inboxFiles} setFiles={setInboxFiles}
            text={inboxText} setText={setInboxText}
            uploading={inboxUploading} onSubmit={onQuickAdd}
          />
        </div>
      </div>
    </div>
  );
}

function FieldContentDrop({ files, setFiles, text, setText, uploading, onSubmit }) {
  const onDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length) setFiles([...(files || []), ...dropped]);
  };
  const onSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles([...(files || []), ...picked]);
  };
  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <label
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50/40 rounded-xl p-4 flex flex-col items-center justify-center gap-1.5 cursor-pointer transition-colors text-center"
        data-testid="mkt-inbox-dropzone"
      >
        <UploadCloud className="w-5 h-5 text-slate-400" />
        <div className="text-sm text-slate-600">
          Déposez vos photos, vidéos ou documents
        </div>
        <div className="text-xs text-slate-400">ou cliquez pour parcourir</div>
        <input type="file" multiple onChange={onSelect} className="hidden"
               data-testid="mkt-inbox-file-input"
               accept="image/*,video/*,application/pdf" />
      </label>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <span key={i}
                  className="text-xs bg-slate-100 border border-slate-200 rounded-md px-2 py-1 flex items-center gap-1.5">
              <ImageIcon className="w-3 h-3" />
              {f.name.length > 24 ? f.name.slice(0, 22) + '…' : f.name}
              <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Que s'est-il passé ? Ex : Remplacement du parquet d'un dojo pour la commune de Mantes-la-Ville."
        className="w-full min-h-[70px] px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid="mkt-inbox-text"
      />
      <button
        onClick={onSubmit}
        disabled={uploading || (!files.length && !text.trim())}
        className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
        data-testid="mkt-inbox-submit"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        {uploading ? 'Envoi…' : 'Ajouter'}
      </button>
    </div>
  );
}

// ==========================================================================
// À VALIDER — vraie preview sociale
// ==========================================================================
function ValidateTab({
  posts, brand, summary,
  planWarnings = [], linkedinStatus = null,
  postPlatformTab, setPostPlatformTab,
  expandedDetails, setExpandedDetails,
  onValidate, onReject, onEdit,
  onValidateAll, validating, onRefresh,
  onGenerateVisual, generating,
  onSchedule, onReschedule, onUnschedule,
  getSelectedPlatforms, onTogglePlatform, savingSelectedByDraft,
  onFindingConfirmed,
}) {
  // V3.1d — Banner LinkedIn (verrou backend Reno Resolve)
  const linkedinBanner = (() => {
    if (!linkedinStatus) return null;
    const orgName = linkedinStatus.preferred_org_name;
    const orgUrn = linkedinStatus.preferred_org_urn;
    const isConfigured = !!(orgUrn && orgUrn.startsWith('urn:li:organization:'));
    if (isConfigured) {
      return (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 flex items-center gap-2 text-xs"
             data-testid="mkt-linkedin-status">
          <Linkedin className="w-4 h-4 text-blue-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-blue-900">
              LinkedIn — Page de publication : {orgName || 'Reno Resolve'}
            </div>
            <div className="text-blue-700/80 truncate" data-testid="mkt-linkedin-urn">
              {orgUrn} · Rôle : ADMINISTRATOR
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 flex items-center gap-2 text-xs"
           data-testid="mkt-linkedin-status-missing">
        <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0" />
        <div className="font-semibold text-amber-900">
          Page LinkedIn non configurée — la publication LinkedIn sera bloquée.
        </div>
      </div>
    );
  })();

  if (!posts.length) {
    return (
      <div className="space-y-4">
        {linkedinBanner}
        {planWarnings.length > 0 && (
          <PlanWarningsBanner warnings={planWarnings} />
        )}
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center"
             data-testid="mkt-validate-empty">
          <Layers className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-700 font-medium">Aucune publication en attente</div>
          <div className="text-sm text-slate-500 mt-1">
            Lancez « Préparer ma semaine » depuis l'onglet Agent IA.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="mkt-validate-view">
      {linkedinBanner}
      {planWarnings.length > 0 && <PlanWarningsBanner warnings={planWarnings} />}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900">{summary.total}</span> publication(s) ·{' '}
          <span className="text-emerald-600 font-medium">{summary.ready_to_validate}</span> vérifiée(s) ·{' '}
          <span className="text-amber-600 font-medium">{summary.attention_needed}</span> attention
        </div>
        <div className="flex gap-2">
          <button
            onClick={onRefresh}
            className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-700"
            data-testid="mkt-validate-refresh-btn"
          >Actualiser</button>
          {summary.ready_to_validate > 0 && (
            <button
              onClick={onValidateAll}
              className="text-sm px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              data-testid="mkt-validate-all-btn"
            >
              Tout valider ({summary.ready_to_validate})
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {posts.map((p, idx) => (
          <PostPreviewCard
            key={p.draft_id}
            post={p}
            brand={brand}
            index={idx}
            selectedPlatform={postPlatformTab[p.draft_id]}
            onSelectPlatform={(plat) =>
              setPostPlatformTab({ ...postPlatformTab, [p.draft_id]: plat })
            }
            detailsOpen={expandedDetails[p.draft_id]}
            onToggleDetails={() =>
              setExpandedDetails({
                ...expandedDetails,
                [p.draft_id]: !expandedDetails[p.draft_id],
              })
            }
            onValidate={() => onValidate(p)}
            onReject={() => onReject(p)}
            onEdit={() => onEdit(p)}
            onSchedule={() => onSchedule(p)}
            onReschedule={() => onReschedule(p)}
            onUnschedule={() => onUnschedule(p)}
            busy={validating === p.draft_id}
            onGenerateVisual={onGenerateVisual}
            generating={generating}
            selectedPlatforms={getSelectedPlatforms(p)}
            onTogglePlatform={(plat) => onTogglePlatform(p, plat)}
            savingSelection={!!savingSelectedByDraft[p.draft_id]}
            onFindingConfirmed={onFindingConfirmed}
          />
        ))}
      </div>
    </div>
  );
}

// V3.1d — Banner amber persistant : warnings du plan courant
function PlanWarningsBanner({ warnings }) {
  if (!warnings || !warnings.length) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
         data-testid="mkt-plan-warnings-banner">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
            Points d'attention sur le plan de la semaine
          </div>
          <ul className="text-xs text-amber-900 space-y-0.5 list-disc pl-4">
            {warnings.slice(0, 6).map((w, i) => (
              <li key={i} data-testid={`mkt-plan-warning-${i}`}>{w}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}


// V3.1d — Badges Reviewer par catégorie (regulatory_claim, aid_guarantee, …)
const FINDING_CATEGORY_META = {
  regulatory_claim: {
    label: 'Réglementation',
    tone: 'bg-purple-50 text-purple-800 border-purple-200',
    icon: ShieldAlert,
  },
  commercial_claim: {
    label: 'Affirmation commerciale',
    tone: 'bg-orange-50 text-orange-800 border-orange-200',
    icon: AlertTriangle,
  },
  unsupported_fact: {
    label: 'Fait à vérifier',
    tone: 'bg-amber-50 text-amber-800 border-amber-200',
    icon: AlertTriangle,
  },
  hallucination_price: {
    label: 'Prix à vérifier',
    tone: 'bg-red-50 text-red-800 border-red-200',
    icon: AlertTriangle,
  },
  aid_guarantee: {
    label: 'Aide garantie — BLOQUANT',
    tone: 'bg-red-50 text-red-800 border-red-300 font-semibold',
    icon: ShieldAlert,
  },
  semantic_drift: {
    label: 'Dérive sémantique',
    tone: 'bg-slate-50 text-slate-700 border-slate-200',
    icon: AlertTriangle,
  },
};

function FindingBadge({ finding, draftId, onConfirmed }) {
  const meta = FINDING_CATEGORY_META[finding.category] || {
    label: finding.category, tone: 'bg-slate-50 text-slate-700 border-slate-200',
    icon: AlertTriangle,
  };
  const Icon = meta.icon;
  const [noteOpen, setNoteOpen] = React.useState(false);
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const isBlocking = finding.severity === 'blocking';
  const isConfirmed = !!finding.confirmed_by_human;
  const canConfirm = isBlocking && finding.overridable && !isConfirmed;
  const nonOverridable = isBlocking && !finding.overridable;

  const confirm = async () => {
    if (!finding.id || !draftId) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/api/social-agent/drafts/${draftId}/findings/${finding.id}/confirm`,
        { confirmation_note: note || null },
      );
      toast.success('Finding confirmé');
      if (onConfirmed) onConfirmed(r.data);
    } catch (e) {
      const msg = extractErrorMessage(e) || 'Erreur confirmation';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`text-[11px] rounded border px-2 py-1 flex items-start gap-1.5 ${meta.tone} ${isConfirmed ? 'opacity-70' : ''}`}
         data-testid={`mkt-finding-${finding.category}`}>
      <Icon className="w-3 h-3 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-medium flex items-center gap-1 flex-wrap">
          <span>{meta.label}</span>
          {isBlocking && !isConfirmed && (
            <span className="ml-1 text-[9px] uppercase tracking-wider text-red-700 font-bold">
              blocking
            </span>
          )}
          {isConfirmed && (
            <span className="ml-1 text-[10px] rounded px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold flex items-center gap-0.5"
                  data-testid={`mkt-finding-${finding.category}-confirmed`}>
              <CheckCircle2 className="w-2.5 h-2.5" /> Confirmé par vous
            </span>
          )}
          {nonOverridable && (
            <span className="ml-1 text-[10px] rounded px-1.5 py-0.5 bg-red-100 text-red-800 border border-red-300 font-semibold">
              non contournable
            </span>
          )}
        </div>
        {finding.human_question && (
          <div className="mt-0.5 italic">{finding.human_question}</div>
        )}
        {finding.excerpt && (
          <div className="mt-0.5 text-slate-700 truncate" title={finding.excerpt}>
            « {finding.excerpt} »
          </div>
        )}
        {isConfirmed && finding.confirmation_note && (
          <div className="mt-1 text-[10px] text-emerald-800 italic">
            Note : {finding.confirmation_note}
          </div>
        )}
        {isConfirmed && finding.confirmed_at && (
          <div className="text-[10px] text-slate-500">
            {new Date(finding.confirmed_at).toLocaleString('fr-FR')}
            {finding.confirmed_by ? ` · ${finding.confirmed_by}` : ''}
          </div>
        )}
        {canConfirm && (
          <div className="mt-1.5 flex flex-col gap-1">
            {noteOpen && (
              <textarea
                data-testid={`mkt-finding-${finding.category}-note`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note optionnelle (ex : 'oui nous proposons ce service')"
                className="text-[11px] rounded border border-slate-300 px-2 py-1 w-full resize-none"
                rows={2}
              />
            )}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={confirm}
                disabled={busy}
                data-testid={`mkt-finding-${finding.category}-confirm-btn`}
                className="text-[10px] rounded px-2 py-1 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 font-medium flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Je confirme
              </button>
              <button
                type="button"
                onClick={() => setNoteOpen((v) => !v)}
                data-testid={`mkt-finding-${finding.category}-note-toggle`}
                className="text-[10px] rounded px-2 py-1 text-slate-700 border border-slate-300 hover:bg-slate-50">
                {noteOpen ? 'Masquer' : 'Ajouter une note'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



function PostPreviewCard({
  post, brand, index, selectedPlatform, onSelectPlatform,
  detailsOpen, onToggleDetails, onValidate, onReject, onEdit, busy,
  onGenerateVisual, generating,
  onSchedule, onReschedule, onUnschedule,
  selectedPlatforms = [], onTogglePlatform, savingSelection = false,
  onFindingConfirmed,
}) {
  const variants = post.platform_variants || [];
  const assetsMap = post.platform_assets || {};
  // V3.1 — RÈGLE UI ABSOLUE : les 3 tabs LinkedIn | Facebook | Instagram
  // s'affichent TOUJOURS, dans cet ordre, même si le draft ne contient qu'une
  // ou deux variantes. Les cases à cocher de publication (elsewhere) restent
  // 100% indépendantes des tabs de preview.
  const ALL_PLATFORMS = ['linkedin', 'facebook', 'instagram'];
  const variantByPlatform = Object.fromEntries(
    variants.map(v => [v.platform, v]),
  );
  const platforms = ALL_PLATFORMS;
  // Plateforme active par défaut : d'abord une qui a un asset réel, sinon la
  // première qui a une variante texte, sinon LinkedIn (premier tab).
  const platformsWithAsset = platforms.filter(p => {
    const pa = assetsMap[p];
    return pa && (pa.asset || pa.asset_type === 'text_only' || pa.needs_visual === true);
  });
  const platformsWithVariant = platforms.filter(p => !!variantByPlatform[p]);
  const defaultActive = platformsWithAsset[0]
                      || platformsWithVariant[0]
                      || platforms[0];
  const active = selectedPlatform && platforms.includes(selectedPlatform)
    ? selectedPlatform : defaultActive;
  // Si aucune variante pour la plateforme active, `variant` reste null
  // et l'affichage bascule sur l'état "Variante non générée" (aucun fallback
  // silencieux vers une autre plateforme).
  const variant = variantByPlatform[active] || null;
  const status = post.review || {};
  // V3.1f — Card grisée (opacity) pour les statuts "rejeté" (draft écarté).
  // Les blocked/needs_attention conservent leur couleur normale car ils
  // restent actionnables (édition/réponse à la question du reviewer).
  const isDimmed = post.status === 'rejected';

  return (
    <div className={`bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
           isDimmed ? 'opacity-60' : ''
         }`}
         data-testid={`mkt-post-card-${index}`}>
      {/* V3.1 — TOUJOURS 3 tabs : LinkedIn | Facebook | Instagram.
          flex-1 sur chacun → réparti equitablement même sur mobile (iPhone SE).
          Le tab d'une plateforme sans variante reste cliquable et rend un
          état "Variante non générée" sans faire d'appel LLM. */}
      <div className="flex border-b border-slate-100 bg-slate-50">
        {platforms.map(plat => {
          const meta = PLATFORM_META[plat] || {};
          const Icon = meta.icon || Instagram;
          const isActive = plat === active;
          const hasVariant = !!variantByPlatform[plat];
          return (
            <button
              key={plat}
              onClick={() => onSelectPlatform(plat)}
              className={`flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 py-2 px-1 text-xs sm:text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-white text-slate-900 border-b-2 border-blue-600'
                  : hasVariant
                    ? 'text-slate-500 hover:text-slate-800'
                    : 'text-slate-400 hover:text-slate-600'
              }`}
              data-testid={`mkt-post-platform-${index}-${plat}`}
              aria-label={meta.label || plat}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{meta.label || plat}</span>
              {!hasVariant && (
                <span
                  className="ml-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0"
                  data-testid={`mkt-post-platform-missing-${index}-${plat}`}
                  title="Variante non générée"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Preview */}
      <SocialPreview
        post={post} variant={variant} platform={active} brand={brand}
        onGenerateVisual={onGenerateVisual ? (opts) => onGenerateVisual(post, active, opts || {}) : null}
        generatingThis={!!(generating && generating[`${post.draft_id}:${active}`])}
      />

      {/* V3.1d — Checkboxes permanentes "Publier sur" (indépendantes des tabs preview) */}
      <div className="border-t border-slate-100 bg-slate-50 px-4 py-2"
           data-testid={`mkt-post-publish-selection-${index}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 shrink-0">
            Publier sur :
          </div>
          {savingSelection && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
          {['linkedin', 'facebook', 'instagram'].map(pl => {
            const hasVariant = !!variantByPlatform[pl];
            const checked = (selectedPlatforms || []).includes(pl);
            const meta = PLATFORM_META[pl] || {};
            const Icon = meta.icon || Instagram;
            return (
              <label
                key={pl}
                className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded border cursor-pointer transition ${
                  !hasVariant
                    ? 'opacity-50 cursor-not-allowed border-slate-200 text-slate-400 bg-white'
                    : checked
                      ? 'bg-blue-50 border-blue-300 text-blue-900'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-blue-200'
                }`}
                data-testid={`mkt-post-publish-toggle-${index}-${pl}`}
              >
                <input
                  type="checkbox"
                  disabled={!hasVariant}
                  checked={checked}
                  onChange={() => onTogglePlatform && onTogglePlatform(pl)}
                  className="w-3 h-3 accent-blue-700"
                  data-testid={`mkt-post-publish-checkbox-${index}-${pl}`}
                />
                <Icon className="w-3 h-3 shrink-0" />
                <span className="capitalize font-medium">{meta.label || pl}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Reviewer status */}
      <div className="border-t border-slate-100 p-4">
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={onToggleDetails}
            className="flex items-center gap-2 text-sm hover:opacity-80"
            data-testid={`mkt-post-status-${index}`}
          >
            {post.status === 'ready_to_validate' && (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-700 font-medium">Prêt à valider</span>
              </>
            )}
            {post.status === 'needs_attention' && (
              <>
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-amber-700 font-medium">Votre attention est nécessaire</span>
              </>
            )}
            {post.status === 'preparing' && (
              <>
                <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                <span className="text-slate-600 font-medium">Préparation…</span>
              </>
            )}
            {post.status === 'validated' && (
              <>
                <CheckCircle2 className="w-4 h-4 text-blue-600" />
                <span className="text-blue-700 font-medium">Validé</span>
              </>
            )}
            {post.status === 'scheduled' && (
              <>
                <CalendarIcon className="w-4 h-4 text-blue-700" />
                <span className="text-blue-800 font-medium">Programmé</span>
              </>
            )}
            {post.status === 'published' && (
              <>
                <Send className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-700 font-medium">Publié</span>
              </>
            )}
            {post.status === 'rejected' && (
              <>
                <AlertTriangle className="w-4 h-4 text-slate-400" />
                <span className="text-slate-500 font-medium line-through">Rejeté</span>
                <span className="text-xs text-slate-400 no-underline">(masqué au prochain run)</span>
              </>
            )}
            {post.status === 'approved' && (
              <>
                <CheckCircle2 className="w-4 h-4 text-blue-600" />
                <span className="text-blue-700 font-medium">Validé (à programmer)</span>
              </>
            )}
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
          </button>
          <div className="flex gap-1.5 flex-wrap justify-end">
            {post.status === 'validated' && (
              <button
                onClick={onSchedule}
                disabled={busy}
                className="text-xs px-3 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white font-medium flex items-center gap-1"
                data-testid={`mkt-post-schedule-${index}`}
              >
                <CalendarIcon className="w-3 h-3" /> Programmer
              </button>
            )}
            {post.status === 'scheduled' && (
              <>
                <button
                  onClick={onReschedule}
                  disabled={busy}
                  className="text-xs px-3 py-1.5 rounded-lg border border-blue-200 hover:bg-blue-50 text-blue-800 flex items-center gap-1"
                  data-testid={`mkt-post-reschedule-${index}`}
                >
                  <CalendarIcon className="w-3 h-3" /> Modifier programmation
                </button>
                <button
                  onClick={onUnschedule}
                  disabled={busy}
                  className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-700"
                  data-testid={`mkt-post-unschedule-${index}`}
                >
                  Déprogrammer
                </button>
              </>
            )}
            {post.status !== 'scheduled' && post.status !== 'validated' && (
              <>
                <button
                  onClick={onEdit}
                  disabled={busy}
                  className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-700 flex items-center gap-1"
                  data-testid={`mkt-post-edit-${index}`}
                >
                  <Edit3 className="w-3 h-3" /> Modifier
                </button>
                <button
                  onClick={onReject}
                  disabled={busy}
                  className="text-xs px-3 py-1.5 rounded-lg border border-red-200 hover:bg-red-50 text-red-700"
                  data-testid={`mkt-post-reject-${index}`}
                >
                  Rejeter
                </button>
                <button
                  onClick={onValidate}
                  disabled={busy || !status.can_validate}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium flex items-center gap-1 ${
                    status.can_validate
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                  data-testid={`mkt-post-validate-${index}`}
                  title={!status.can_validate ? "Une information doit être confirmée avant validation" : ""}
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                  Valider
                </button>
              </>
            )}
          </div>
        </div>

        {/* V3.0 Bloc 1 — Affichage programmation */}
        {post.status === 'scheduled' && post.schedule?.scheduled_at_local && (
          <div className="mt-3 pt-3 border-t border-slate-100 text-sm bg-blue-50 -mx-4 -mb-4 px-4 pb-4 rounded-b-2xl"
               data-testid={`mkt-post-scheduled-info-${index}`}>
            <div className="flex items-center gap-2 text-blue-800 font-medium">
              <CalendarIcon className="w-4 h-4" />
              Programmé le {formatLocalDate(post.schedule.scheduled_at_local)}
              <span className="text-xs text-blue-600 font-normal">
                ({post.schedule.timezone})
              </span>
            </div>
            <div className="mt-1.5 text-xs text-slate-600">
              Plateformes :{' '}
              {(post.schedule.publication_targets || []).map(t => (
                <span key={t.platform} className="inline-flex items-center gap-1 mr-2">
                  <span className="capitalize">{t.platform}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-500">{t.status}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Détails (au clic seulement) */}
        {detailsOpen && (
          <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600 space-y-2"
               data-testid={`mkt-post-details-${index}`}>
            {/* V3.1d — Liste complète des findings Reviewer (MPA, aid_guarantee, …) */}
            {(status.findings || []).length > 0 && (
              <div className="space-y-1.5" data-testid={`mkt-post-findings-${index}`}>
                <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-1">
                  <ShieldAlert className="w-3.5 h-3.5" /> Vérifications ({status.findings.length})
                </div>
                {status.findings.map((f, fi) => (
                  <FindingBadge
                    key={f.id || fi}
                    finding={f}
                    draftId={post.draft_id}
                    onConfirmed={onFindingConfirmed}
                  />
                ))}
              </div>
            )}
            {status.status === 'verified' && (
              <div className="text-emerald-700">
                L'agent a vérifié la cohérence factuelle et sémantique de ce post.
                Vous pouvez le valider.
              </div>
            )}
            {status.status === 'not_reviewed' && (
              <div className="text-slate-500 italic">
                Ce post n'a pas encore été inspecté. Lancez « Préparer ma semaine » pour lancer la vérification.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SocialPreview({ post, variant, platform, brand, onGenerateVisual, generatingThis }) {
  const meta = PLATFORM_META[platform] || {};
  const Icon = meta.icon || Instagram;
  const brandName = brand?.name || 'Reno Resolve';
  const brandInitial = (brandName[0] || 'R').toUpperCase();
  const brandColor = brand?.primary_color || '#1C49A6';

  // V3.1 — Aucune variante générée pour cette plateforme : afficher un
  // état explicite sans inventer de texte. Ne PAS déclencher d'appel LLM.
  if (!variant) {
    return (
      <div className="p-6 text-center" data-testid={`mkt-post-preview-missing-${platform}`}>
        <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
          <Icon className="w-6 h-6 text-amber-500" />
        </div>
        <div className="text-sm font-semibold text-slate-800">
          Variante {meta.label || platform} non générée
        </div>
        <div className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
          Ce brouillon n'a pas encore de texte pour {meta.label || platform}.
          Utilise « Régénérer la semaine » ou « Générer les variantes
          manquantes » pour compléter les 3 plateformes.
        </div>
      </div>
    );
  }

  const hashtags = (variant?.hashtags || []).slice(0, 8);
  const text = [variant?.hook, variant?.body].filter(Boolean).join('\n\n');
  const medias = post.medias || [];
  const platformAsset = (post.platform_assets || {})[platform] || null;
  const assetTypePlat = platformAsset?.asset_type;
  const assetGenerated = platformAsset?.asset;         // {url, kind, ai_modified}
  const isTextOnlyForPlatform = assetTypePlat === 'text_only';
  const needsVisualForPlatform = platformAsset?.needs_visual === true;
  const format = post.creative_brief?.recommended_asset_type || variant?.format || 'single_image';
  const aspectClass = platform === 'instagram'
    ? 'aspect-[4/5]'
    : platform === 'linkedin'
      ? 'aspect-[1.91/1]'
      : 'aspect-[16/9]';

  // Combine medias (real photos) + generated asset if any.
  // Un asset backend est considéré "généré" (donc pas de badge Photo optimisée
  // ni bouton Voir original) UNIQUEMENT si son kind n'est pas une photo terrain
  // réellement optimisée par notre pipeline (enhanced_real_photo).
  const mediaList = [...medias];
  // V3.0 — Bloc final : expose TOUS les assets du carousel dans le preview
  // (auparavant seul le premier était ajouté).
  const assetsList = platformAsset?.assets && platformAsset.assets.length > 0
    ? platformAsset.assets : (assetGenerated ? [assetGenerated] : []);
  for (let i = assetsList.length - 1; i >= 0; i -= 1) {
    const a = assetsList[i];
    const isRealPhoto = a.kind === 'enhanced_real_photo';
    // Anti-doublon : si un medias[] contient déjà ce derivative, on l'ignore
    const already = mediaList.some(m =>
      (m.derivative && a.url && m.derivative.url === a.url)
      || (a.media_id && m.media_id === a.media_id),
    );
    if (already) continue;
    mediaList.unshift({
      media_id: a.media_id || `gen-${platform}-${i}`,
      type: 'image',
      mime_type: 'image/jpeg',
      url: null,
      derivative: {
        url: a.url, ai_modified: a.ai_modified,
        operations: [], platform, ratio: a.ratio,
      },
      has_derivative: true,
      generated: !isRealPhoto,
      generated_kind: a.kind,
      legacy_da: platformAsset?.regenerate_recommended === true
                   || (a.is_new_da === false),
      style_version: a.style_version,
      role: a.role,
    });
  }

  return (
    <div className="p-4" data-testid={`mkt-post-preview-${platform}`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm"
          style={{ backgroundColor: brandColor }}
        >
          {brand?.logo_url ? (
            <img src={brand.logo_url} alt={brandName} className="w-full h-full rounded-full object-cover" />
          ) : brandInitial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-slate-900 truncate">{brandName}</div>
          <div className="text-[11px] text-slate-500 flex items-center gap-1">
            <Icon className="w-3 h-3" /> {meta.label || platform}
          </div>
        </div>
      </div>

      {/* Zone média — MASQUÉE si text_only volontaire (LinkedIn) */}
      {!isTextOnlyForPlatform && (
        <MediaBlock
          medias={mediaList}
          needsVisual={needsVisualForPlatform}
          aspectClass={aspectClass}
          platform={platform}
          format={format}
          onGenerateVisual={onGenerateVisual}
          generatingThis={generatingThis}
        />
      )}

      {/* Texte complet */}
      <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
        {text || <span className="italic text-slate-400">Aucun texte</span>}
      </div>
      {variant?.cta && (
        <div className="text-sm text-blue-800 font-medium mt-2">→ {variant.cta}</div>
      )}
      {hashtags.length > 0 && (
        <div className="text-sm text-blue-600 mt-2 space-x-1">
          {hashtags.map(h => (
            <span key={h}>#{h}</span>
          ))}
        </div>
      )}
      <div className="text-[11px] text-slate-400 mt-3 flex items-center gap-1">
        <CalendarIcon className="w-3 h-3" />
        Programmation à confirmer
      </div>
    </div>
  );
}

function MediaBlock({ medias, needsVisual, aspectClass, platform, format,
                       onGenerateVisual, generatingThis }) {
  const [idx, setIdx] = useState(0);
  const [showOriginal, setShowOriginal] = useState({});
  const [genError, setGenError] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const buildUrl = (u) => {
    if (!u) return null;
    return u.startsWith('http') ? u : `${backend}${u}`;
  };
  const triggerGenerate = async (opts = {}) => {
    if (!onGenerateVisual || generatingThis) return;
    setGenError(false);
    try {
      await onGenerateVisual(opts);
    } catch (_) {
      setGenError(true);
    }
  };
  // V2.9.3 — repère si l'asset actuel est ancienne DA
  const hasLegacyDA = medias.some(m => m.legacy_da === true);
  // ✨ Visuel à créer
  if (needsVisual && medias.length === 0) {
    return (
      <div className={`${aspectClass} rounded-xl overflow-hidden mb-3 bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center`}
           data-testid={`mkt-preview-needs-visual-${platform}`}>
        <div className="text-blue-800 text-center px-6">
          <Wand2 className="w-8 h-8 mx-auto mb-1 opacity-80" />
          {generatingThis ? (
            <>
              <div className="text-sm font-medium">Création du visuel…</div>
              <Loader2 className="w-4 h-4 mx-auto mt-2 animate-spin" />
            </>
          ) : genError ? (
            <>
              <div className="text-sm font-medium text-red-600">
                Impossible de générer le visuel
              </div>
              <button
                onClick={() => triggerGenerate({ force: false })}
                className="mt-2 text-[11px] px-3 py-1 rounded bg-blue-700 hover:bg-blue-800 text-white font-medium"
                data-testid={`mkt-preview-retry-visual-${platform}`}
              >Réessayer</button>
            </>
          ) : (
            <>
              <div className="text-sm font-medium">Visuel recommandé par l'agent</div>
              <div className="text-xs text-blue-600 mt-1 italic">
                Aucun média terrain sélectionné pour ce post
              </div>
              <button
                onClick={() => triggerGenerate({ force: false })}
                disabled={!onGenerateVisual}
                className={`mt-2 text-[11px] px-3 py-1 rounded font-medium ${
                  onGenerateVisual
                    ? 'bg-blue-700 hover:bg-blue-800 text-white'
                    : 'bg-blue-200 text-blue-600 cursor-not-allowed'
                }`}
                data-testid={`mkt-preview-generate-visual-${platform}`}
              >Générer le visuel</button>
            </>
          )}
        </div>
      </div>
    );
  }
  // V3.0 — Aucun média ET pas de recommandation IA explicite :
  // proposer quand même la génération d'un visuel (bouton actif).
  if (!medias.length) {
    return (
      <div className={`${aspectClass} rounded-xl overflow-hidden mb-3 bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center`}
           data-testid={`mkt-preview-empty-generate-${platform}`}>
        <div className="text-blue-800 text-center px-6">
          <Wand2 className="w-8 h-8 mx-auto mb-1 opacity-80" />
          {generatingThis ? (
            <>
              <div className="text-sm font-medium">Création du visuel…</div>
              <Loader2 className="w-4 h-4 mx-auto mt-2 animate-spin" />
            </>
          ) : genError ? (
            <>
              <div className="text-sm font-medium text-red-600">
                La génération a échoué
              </div>
              <button
                onClick={() => triggerGenerate({ force: false })}
                className="mt-2 text-[11px] px-3 py-1 rounded bg-blue-700 hover:bg-blue-800 text-white font-medium"
                data-testid={`mkt-preview-retry-visual-${platform}`}
              >Réessayer</button>
            </>
          ) : (
            <>
              <div className="text-sm font-medium">Aucun média</div>
              <div className="text-xs text-blue-600 mt-1 italic">
                Ce post n'a pas encore de visuel
              </div>
              <button
                onClick={() => triggerGenerate({ force: false })}
                disabled={!onGenerateVisual}
                className={`mt-2 text-[11px] px-3 py-1 rounded font-medium flex items-center gap-1 mx-auto ${
                  onGenerateVisual
                    ? 'bg-blue-700 hover:bg-blue-800 text-white'
                    : 'bg-blue-200 text-blue-600 cursor-not-allowed'
                }`}
                data-testid={`mkt-preview-generate-empty-${platform}`}
              >
                <Wand2 className="w-3 h-3" /> Générer un visuel
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
  const isCarousel = medias.length > 1 || format === 'carousel';
  const current = medias[Math.min(idx, medias.length - 1)];
  const hasDeriv = !!current?.derivative && !showOriginal[current.media_id];
  const url = buildUrl(hasDeriv ? current.derivative.url : current?.url);
  const isImage = (current?.type || '').startsWith('image')
    || (current?.mime_type || '').startsWith('image');
  const isVideo = (current?.type || '').startsWith('video')
    || (current?.mime_type || '').startsWith('video');

  const toggleOriginal = () => setShowOriginal({
    ...showOriginal, [current.media_id]: !showOriginal[current.media_id],
  });

  return (
    <div className={`${aspectClass} rounded-xl overflow-hidden mb-3 relative bg-slate-900`}
         data-testid={`mkt-preview-media-${platform}`}>
      {url && isVideo ? (
        <video src={url} controls className="w-full h-full object-cover"
               data-testid={`mkt-preview-video-${platform}`} />
      ) : url && isImage ? (
        <img
          src={url}
          alt={current.filename || 'media'}
          className="w-full h-full object-cover"
          data-testid={`mkt-preview-image-${platform}`}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-400">
          <ImageIcon className="w-10 h-10" />
        </div>
      )}
      {/* Badge Photo optimisée (uniquement pour photos terrain réellement optimisées) */}
      {current?.derivative && !current?.generated && !showOriginal[current.media_id] && (
        <div className="absolute top-2 left-2 flex items-center gap-1.5"
             data-testid={`mkt-preview-badge-optimized-${platform}`}>
          <span className="bg-emerald-600/90 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium shadow flex items-center gap-1">
            <Wand2 className="w-2.5 h-2.5" /> Photo optimisée
          </span>
        </div>
      )}
      {/* V2.9.3 — Bandeau "Ancienne DA · Régénérer avec la nouvelle DA" */}
      {hasLegacyDA && (
        <div className="absolute bottom-0 left-0 right-0 bg-amber-500/95 text-white px-3 py-2 flex items-center justify-between shadow-lg"
             data-testid={`mkt-preview-legacy-da-banner-${platform}`}>
          <div className="text-[11px] font-medium flex-1">
            Ancienne direction artistique · nouveau style disponible
          </div>
          <button
            onClick={() => triggerGenerate({ force: true })}
            disabled={generatingThis || !onGenerateVisual}
            className={`text-[11px] px-3 py-1 rounded font-semibold ${
              generatingThis
                ? 'bg-white/40 text-white/80 cursor-wait'
                : 'bg-white text-amber-700 hover:bg-amber-50'
            }`}
            data-testid={`mkt-preview-regenerate-new-da-${platform}`}
          >
            {generatingThis ? 'Régénération…' : 'Régénérer avec la nouvelle DA'}
          </button>
        </div>
      )}
      {/* V3.0 — Bouton Régénérer l'image sur asset généré déjà en nouvelle DA */}
      {!hasLegacyDA && current?.generated && (
        <button
          onClick={(e) => { e.stopPropagation(); triggerGenerate({ force: true }); }}
          disabled={generatingThis}
          className={`absolute top-2 right-2 text-[10px] px-2 py-1 rounded font-medium flex items-center gap-1 shadow ${
            generatingThis
              ? 'bg-blue-200 text-blue-600 cursor-wait'
              : 'bg-white/90 text-blue-800 hover:bg-white'
          }`}
          data-testid={`mkt-preview-regenerate-${platform}`}
          title="Génère une nouvelle proposition visuelle"
        >
          {generatingThis
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <Wand2 className="w-3 h-3" />}
          {generatingThis ? 'Régénération…' : "Régénérer l'image"}
        </button>
      )}
      {current?.derivative && !current?.generated && (
        <button
          onClick={(e) => { e.stopPropagation(); toggleOriginal(); }}
          className="absolute bottom-2 left-2 text-[10px] bg-black/60 hover:bg-black/80 text-white rounded px-2 py-0.5"
          data-testid={`mkt-preview-toggle-original-${platform}`}
        >
          {showOriginal[current.media_id] ? 'Voir optimisée' : 'Voir original'}
        </button>
      )}
      {isCarousel && medias.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + medias.length) % medias.length); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5"
            data-testid={`mkt-carousel-prev-${platform}`}
            aria-label="Précédent"
          ><ChevronRight className="w-4 h-4 rotate-180" /></button>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % medias.length); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5"
            data-testid={`mkt-carousel-next-${platform}`}
            aria-label="Suivant"
          ><ChevronRight className="w-4 h-4" /></button>
          <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-medium rounded-full px-2 py-0.5"
               data-testid={`mkt-carousel-counter-${platform}`}>
            {idx + 1} / {medias.length}
          </div>
        </>
      )}
    </div>
  );
}

// ==========================================================================
// EDITOR MODAL — édition simple master_message + body par variante
// ==========================================================================
function EditorModal({ editing, setEditing, onSave }) {
  const updateVariant = (idx, field, value) => {
    const next = [...(editing.platform_variants || [])];
    next[idx] = { ...next[idx], [field]: value };
    setEditing({ ...editing, platform_variants: next });
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
         onClick={() => setEditing(null)} data-testid="mkt-editor-modal">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 space-y-4 shadow-2xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Modifier la publication</h3>
          <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Message principal
          </label>
          <textarea
            value={editing.master_message || ''}
            onChange={(e) => setEditing({ ...editing, master_message: e.target.value })}
            className="w-full mt-1 min-h-[80px] px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="mkt-editor-master-message"
          />
        </div>
        {(editing.platform_variants || []).map((v, i) => (
          <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2"
               data-testid={`mkt-editor-variant-${v.platform}`}>
            <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-1.5">
              {(PLATFORM_META[v.platform]?.icon || Instagram) &&
                React.createElement(PLATFORM_META[v.platform]?.icon || Instagram, { className: 'w-3.5 h-3.5' })}
              {PLATFORM_META[v.platform]?.label || v.platform}
            </div>
            <input
              value={v.hook || ''}
              onChange={(e) => updateVariant(i, 'hook', e.target.value)}
              placeholder="Accroche"
              className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
            />
            <textarea
              value={v.body || ''}
              onChange={(e) => updateVariant(i, 'body', e.target.value)}
              placeholder="Corps du post"
              className="w-full min-h-[70px] px-2 py-1.5 border border-slate-200 rounded text-sm"
            />
            <input
              value={v.cta || ''}
              onChange={(e) => updateVariant(i, 'cta', e.target.value)}
              placeholder="Appel à l'action"
              className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
            />
          </div>
        ))}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={() => setEditing(null)}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
            Annuler
          </button>
          <button onClick={onSave}
                  className="px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white font-medium"
                  data-testid="mkt-editor-save">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// SETTINGS PANEL — accès discret aux avancés
// ==========================================================================
function SettingsPanel({ onClose, brand }) {
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-end"
         onClick={onClose} data-testid="mkt-settings-panel">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white h-full w-full max-w-md p-6 overflow-y-auto shadow-2xl space-y-6"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Paramètres de l'agent</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <section className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Marque</div>
          <div className="p-3 bg-slate-50 rounded-lg text-sm">
            <div className="font-semibold text-slate-900">{brand?.name || '—'}</div>
            {brand?.positioning && (
              <div className="text-slate-600 text-xs mt-1 italic">{brand.positioning}</div>
            )}
          </div>
        </section>

        <section className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Préférences éditoriales
          </div>
          <p className="text-sm text-slate-500">
            Ton, plateformes, sujets à privilégier ou éviter — bientôt disponibles ici.
          </p>
        </section>

        <MascotSection />

        <section className="space-y-2 pt-4 border-t border-slate-100">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Avancé</div>
          <p className="text-sm text-slate-500">
            Accès aux réglages techniques de l'agent (Brand Brain, Strategy, capacités,
            Planner, Reviewer). Réservé au diagnostic.
          </p>
          <Link
            to="/marketing/advanced"
            className="inline-flex items-center gap-2 mt-1 text-sm font-medium text-blue-800 hover:text-blue-900"
            data-testid="mkt-advanced-link"
          >
            Ouvrir l'interface technique <ChevronRight className="w-4 h-4" />
          </Link>
        </section>
      </div>
    </div>
  );
}

// V3.0-cost — Mascotte de marque (upload/preview/toggle/delete)
function MascotSection() {
  const [state, setState] = useState({
    loading: true, media_id: null, enabled: false, preview_url: null,
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const cacheBust = React.useRef(Date.now());

  const load = async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const r = await api.get('/community-manager/brand/mascot');
      setState({
        loading: false,
        media_id: r.data?.brand_mascot_media_id || null,
        enabled: !!r.data?.brand_mascot_enabled,
        preview_url: r.data?.preview_url || null,
        notes: r.data?.brand_mascot_notes || '',
      });
    } catch (_) {
      setState(s => ({ ...s, loading: false }));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/community-manager/brand/mascot', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      cacheBust.current = Date.now();
      toast.success('Mascotte importée');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import impossible');
    }
    setBusy(false);
  };

  const doDelete = async () => {
    if (!window.confirm('Supprimer la mascotte importée ?')) return;
    setBusy(true);
    try {
      await api.delete('/community-manager/brand/mascot');
      toast.success('Mascotte supprimée');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression impossible');
    }
    setBusy(false);
  };

  const doToggle = async () => {
    setBusy(true);
    try {
      await api.patch('/community-manager/brand/mascot/toggle', {
        enabled: !state.enabled,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action impossible');
    }
    setBusy(false);
  };

  const previewSrc = state.preview_url
    ? `${process.env.REACT_APP_BACKEND_URL}${state.preview_url}?v=${cacheBust.current}`
    : null;

  return (
    <section className="space-y-3 pt-4 border-t border-slate-100"
             data-testid="mkt-settings-mascot">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
        Mascotte de marque
      </div>

      <div className="flex items-start gap-3">
        <div className="w-24 h-24 rounded-xl bg-slate-50 border border-slate-200
                       flex items-center justify-center overflow-hidden shrink-0">
          {previewSrc ? (
            <img src={previewSrc} alt="mascotte"
                 className="w-full h-full object-contain"
                 data-testid="mkt-mascot-preview" />
          ) : (
            <ImageIcon className="w-8 h-8 text-slate-300" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input ref={inputRef} type="file"
                 accept="image/png,image/jpeg,image/webp"
                 className="hidden"
                 data-testid="mkt-mascot-file-input"
                 onChange={(e) => doUpload(e.target.files?.[0])} />
          <div className="flex gap-2 flex-wrap">
            {!state.media_id ? (
              <button onClick={() => inputRef.current?.click()}
                      disabled={busy}
                      className="text-xs px-3 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white font-medium disabled:opacity-50"
                      data-testid="mkt-mascot-import-btn">
                {busy ? 'Import…' : 'Importer la mascotte'}
              </button>
            ) : (
              <>
                <button onClick={() => inputRef.current?.click()}
                        disabled={busy}
                        className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                        data-testid="mkt-mascot-replace-btn">
                  Remplacer
                </button>
                <button onClick={doDelete} disabled={busy}
                        className="text-xs px-3 py-1.5 rounded-lg border border-red-200 hover:bg-red-50 text-red-700 disabled:opacity-50"
                        data-testid="mkt-mascot-delete-btn">
                  Supprimer
                </button>
              </>
            )}
          </div>

          {state.media_id && (
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={state.enabled}
                     onChange={doToggle} disabled={busy}
                     data-testid="mkt-mascot-toggle" />
              Utiliser la mascotte dans les visuels
            </label>
          )}

          <p className="text-[11px] text-slate-500 italic">
            Utilisée uniquement lorsque le Directeur Créatif juge sa présence pertinente.
            PNG transparent recommandé (JPG et WebP acceptés, 8 Mo max).
          </p>
        </div>
      </div>
    </section>
  );
}



// ==========================================================================
// PLACEHOLDERS — Calendrier / Bibliothèque / Insights
// ==========================================================================
// V3.0 Bloc 1 — Helpers
function formatLocalDate(isoLocal) {
  if (!isoLocal) return '';
  try {
    const d = new Date(isoLocal);
    return d.toLocaleString('fr-FR', {
      dateStyle: 'full', timeStyle: 'short',
    });
  } catch (_) {
    return isoLocal;
  }
}

// V3.0 Bloc 1 — Modal de programmation
function ScheduleModal({ scheduling, initialSelected, onClose, onSubmit }) {
  const { post, mode } = scheduling;
  const isReschedule = mode === 'reschedule';
  const currentTargets = post.schedule?.publication_targets || [];
  const currentTz = post.schedule?.timezone || 'Europe/Paris';
  const currentLocal = post.schedule?.scheduled_at_local || '';
  const variantPlatforms = (post.platform_variants || []).map(v => v.platform);

  const defaultLocal = (currentLocal && currentLocal.slice(0, 16)) || (() => {
    const d = new Date(Date.now() + 24 * 3600 * 1000);
    d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 16);
  })();
  // V3.1d — Priorité stricte : draft.selected_platforms > publication_targets
  // > variantPlatforms. Ne réinvente JAMAIS une sélection différente à chaque
  // ouverture du modal.
  const initialPlats = (initialSelected && initialSelected.length)
    ? initialSelected
    : (isReschedule && currentTargets.length
        ? currentTargets.map(t => t.platform)
        : variantPlatforms);

  const [dtLocal, setDtLocal] = useState(defaultLocal);
  const [tz, setTz] = useState(currentTz);
  const [plats, setPlats] = useState(initialPlats);
  const [submitting, setSubmitting] = useState(false);

  const togglePlatform = (p) => {
    if (plats.includes(p) && plats.length === 1) {
      toast.error("Sélectionne au moins une plateforme.");
      return;
    }
    setPlats(plats.includes(p) ? plats.filter(x => x !== p) : [...plats, p]);
  };

  const submit = async () => {
    if (!dtLocal) { toast.error('Choisissez une date et une heure.'); return; }
    if (!plats.length) { toast.error('Sélectionnez au moins une plateforme.'); return; }
    setSubmitting(true);
    await onSubmit({
      draft_id: post.draft_id,
      scheduled_at_local: dtLocal,
      timezone: tz,
      platforms: plats,
      isReschedule,
    });
    setSubmitting(false);
  };

  const tzOptions = [
    'Europe/Paris', 'Europe/London', 'Europe/Madrid', 'Europe/Brussels',
    'America/New_York', 'America/Los_Angeles', 'UTC',
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="mkt-schedule-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            {isReschedule ? 'Modifier la programmation' : 'Programmer la publication'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
                  data-testid="mkt-schedule-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="text-sm text-slate-600 truncate">
          {post.master_angle || post.master_message}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
              Date et heure locale
            </label>
            <input
              type="datetime-local" value={dtLocal}
              onChange={(e) => setDtLocal(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="mkt-schedule-datetime"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
              Fuseau horaire
            </label>
            <select
              value={tz} onChange={(e) => setTz(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="mkt-schedule-timezone"
            >
              {tzOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
              Plateformes
            </label>
            <div className="mt-1 flex flex-wrap gap-2">
              {variantPlatforms.map(p => (
                <button
                  key={p}
                  onClick={() => togglePlatform(p)}
                  className={`text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1 capitalize ${
                    plats.includes(p)
                      ? 'bg-blue-700 text-white border-blue-700'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`mkt-schedule-platform-${p}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose}
                  className="text-sm px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-100"
                  data-testid="mkt-schedule-cancel">
            Annuler
          </button>
          <button
            onClick={submit} disabled={submitting}
            className="text-sm px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white font-medium flex items-center gap-1"
            data-testid="mkt-schedule-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarIcon className="w-4 h-4" />}
            {isReschedule ? 'Enregistrer' : 'Programmer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// V3.0 Bloc 1 — Calendrier V2 (source = drafts V2)
function CalendarTabV2() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/community-manager/calendar', {
        params: { year, month, display_tz: 'Europe/Paris' },
      });
      setEntries(r.data?.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement calendrier impossible');
    }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const monthLabel = new Date(year, month - 1, 1).toLocaleString('fr-FR', {
    month: 'long', year: 'numeric',
  });

  const prevMonth = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); }
    else setMonth(month + 1);
  };

  const byDay = {};
  for (const e of entries) {
    const key = (e.scheduled_at_local || '').slice(0, 10);
    if (!key) continue;
    (byDay[key] = byDay[key] || []).push(e);
  }
  const days = Object.keys(byDay).sort();

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="mkt-calendar-v2">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={prevMonth} className="text-slate-500 hover:text-slate-700 p-1"
                  data-testid="mkt-calendar-prev">
            <ChevronRight className="w-5 h-5 rotate-180" />
          </button>
          <h3 className="text-lg font-semibold text-slate-900 capitalize" data-testid="mkt-calendar-month">
            {monthLabel}
          </h3>
          <button onClick={nextMonth} className="text-slate-500 hover:text-slate-700 p-1"
                  data-testid="mkt-calendar-next">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="text-sm text-slate-500">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `${entries.length} publication(s)`}
        </div>
      </div>

      {!loading && entries.length === 0 && (
        <div className="text-center py-10 text-slate-500 text-sm">
          Aucune publication programmée ce mois-ci.
        </div>
      )}

      <div className="space-y-3">
        {days.map(day => (
          <div key={day} className="border border-slate-100 rounded-xl p-3" data-testid={`mkt-calendar-day-${day}`}>
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-800 mb-2">
              {new Date(day).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            <div className="space-y-2">
              {byDay[day].map(e => (
                <div key={e.draft_id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2.5"
                     data-testid={`mkt-calendar-entry-${e.draft_id}`}>
                  <div className="text-sm font-medium text-slate-700 w-16">
                    {(e.scheduled_at_local || '').slice(11, 16)}
                  </div>
                  <div className="flex-1 text-sm text-slate-800 truncate">{e.title || 'Publication'}</div>
                  <div className="flex gap-1.5">
                    {(e.publication_targets || []).map(t => (
                      <span key={t.platform} className={`text-xs px-2 py-0.5 rounded-md ${
                        t.status === 'published' ? 'bg-emerald-100 text-emerald-700'
                          : t.status === 'failed' ? 'bg-red-100 text-red-700'
                          : 'bg-blue-100 text-blue-800'
                      } capitalize`}>
                        {t.platform}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LibraryTabPlaceholder() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center"
         data-testid="mkt-library-placeholder">
      <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
      <div className="text-slate-700 font-medium">Bibliothèque</div>
      <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        Vos photos, vidéos et documents terrain. Ajoutez du contenu depuis l'onglet Agent IA.
      </div>
    </div>
  );
}

function InsightsTabPlaceholder() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center"
         data-testid="mkt-insights-placeholder">
      <Eye className="w-12 h-12 text-slate-300 mx-auto mb-3" />
      <div className="text-slate-700 font-medium">Insights</div>
      <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        Analytics natifs LinkedIn et Meta — à venir.
      </div>
    </div>
  );
}
